// The show form's typeahead hooks — venues from Google Places, lineup acts from the Spotify catalogue — over the
// tenant-keyed query wrappers. Keys are ['band', bandId, 'lookup', …] so a band switch scorches them with
// everything else. They replaced the Bandsintown hooks when that integration was retired (2026-10-01): its terms
// forbid commercial use, so nothing here calls Bandsintown.
//
// PROVIDER DEGRADE ≠ ERROR: both return `{items, available}`; `available === false` means there is no source right
// now (unkeyed host, vendor outage, rate trip) and the caller must fall back to a PLAIN-TEXT input — never an
// error state. The queries enable ONLY at q.length >= 2 and the server caches on top of the client debounce.
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandQuery } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

type VenueSuggestionsResponse = components["schemas"]["VenueSuggestionsResponse"];
type ArtistSuggestionsResponse = components["schemas"]["ArtistSuggestionsResponse"];

/** Below this length a query is never sent — the vendor (and its cache) only sees meaningful prefixes. */
export const MIN_AUTOCOMPLETE_LENGTH = 2;

/** GET venue suggestions for `q` (Google Places). Enabled only at length ≥ 2; a degrade sets `available:false`. */
export function useVenueAutocomplete(q: string) {
  const { api } = useApp();
  const query = q.trim();
  return useBandQuery<VenueSuggestionsResponse>(
    ["lookup", "venues", query],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/lookup/venues", {
        params: { path: { bandId }, query: { q: query } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: query.length >= MIN_AUTOCOMPLETE_LENGTH, staleTime: 60_000 },
  );
}

/** GET lineup-act suggestions for `q` (Spotify catalogue). */
export function useArtistAutocomplete(q: string) {
  const { api } = useApp();
  const query = q.trim();
  return useBandQuery<ArtistSuggestionsResponse>(
    ["lookup", "artists", query],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/lookup/artists", {
        params: { path: { bandId }, query: { q: query } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: query.length >= MIN_AUTOCOMPLETE_LENGTH, staleTime: 60_000 },
  );
}
